import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../redux/store';
import { getAccessToken, isAuthenticated } from '../Backend/services/authService';
import axios from 'axios';
import CONFIG from '../config/environment';

const API_BASE_URL = CONFIG.BACKEND_URL;

// Pages where we don't show the privacy policy modal
const EXCLUDED_PATHS = ['/login', '/register', '/signup', '/reset-password', '/'];

interface PrivacyPolicyStatus {
  accepted: boolean;
  accepted_at: string | null;
  isLoading: boolean;
  error: string | null;
}

export const usePrivacyPolicyCheck = () => {
  const [status, setStatus] = useState<PrivacyPolicyStatus>({
    accepted: true, // Default to true to avoid flash
    accepted_at: null,
    isLoading: true,
    error: null
  });

  const userProfile = useSelector((state: RootState) => state.userProfile.userProfile);
  const location = useLocation();

  // Check if current path is excluded
  const isExcludedPath = EXCLUDED_PATHS.some(path =>
    location.pathname === path || location.pathname.startsWith('/reset-password')
  );

  const checkStatus = useCallback(async () => {
    // Don't check on excluded paths
    if (isExcludedPath) {
      setStatus(prev => ({ ...prev, isLoading: false, accepted: true }));
      return;
    }

    // Only check if user is authenticated
    if (!isAuthenticated() || !userProfile) {
      setStatus(prev => ({ ...prev, isLoading: false, accepted: true }));
      return;
    }

    try {
      const token = getAccessToken();
      if (!token) {
        setStatus(prev => ({ ...prev, isLoading: false, accepted: true }));
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/backend/privacy-policy/status/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );

      if (response.data.status === 'success') {
        setStatus({
          accepted: response.data.accepted,
          accepted_at: response.data.accepted_at,
          isLoading: false,
          error: null
        });
      }
    } catch (error) {
      console.error('Erro ao verificar política de privacidade:', error);
      // On error, allow access (don't block the user)
      setStatus(prev => ({
        ...prev,
        isLoading: false,
        accepted: true,
        error: 'Erro ao verificar status'
      }));
    }
  }, [userProfile, isExcludedPath]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const markAsAccepted = useCallback(() => {
    setStatus(prev => ({
      ...prev,
      accepted: true,
      accepted_at: new Date().toISOString()
    }));
  }, []);

  return {
    ...status,
    refetch: checkStatus,
    markAsAccepted
  };
};
